import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { showError, showSuccess } from "../toaster/store/slice";
import { hasRole } from "../../utils/roles";
import { RoleCode } from "../users/store/slice";
import { VehicleTypeCode } from "../vehicle-types/store/slice";
import styles from "./invoiceImportPage.module.scss";

type ClientCode = "po" | "irish";

type RouteMappingKey = "DOVER-CALAIS" | "CALAIS-DOVER";

type RouteLookup = {
  ferry_id: string;
  company_name: string;
};

const CLIENT_ROUTE_MAPPINGS: Record<
  ClientCode,
  Record<RouteMappingKey, RouteLookup>
> = {
  po: {
    "DOVER-CALAIS": {
      ferry_id: "DOVER-CALAIS",
      company_name: "Pando Short Sea Ferries Limited",
    },
    "CALAIS-DOVER": {
      ferry_id: "CALAIS-DOVER",
      company_name: "Pando Short Sea Ferries Limited",
    },
  },
  irish: {
    "DOVER-CALAIS": {
      ferry_id: "DOVER-CALAIS IRISH",
      company_name: "Irish Ferries LTD",
    },
    "CALAIS-DOVER": {
      ferry_id: "CALAIS-DOVER IRISH",
      company_name: "Irish Ferries LTD",
    },
  },
};

const normalizePort = (port: string) => port.trim().toUpperCase();

const normalizeLookupValue = (value: string) => value.trim().toUpperCase();

const getRouteMappingKey = (
  from: string,
  to: string
): RouteMappingKey | null => {
  const fromPort = normalizePort(from);
  const toPort = normalizePort(to);

  if (fromPort === "DOVER" && toPort === "CALAIS") {
    return "DOVER-CALAIS";
  }

  if (fromPort === "CALAIS" && toPort === "DOVER") {
    return "CALAIS-DOVER";
  }

  return null;
};

type SelectOption = { id: number; name: string };
type VehicleTypeOption = { id: number; name: string; code: VehicleTypeCode };
type RouteOption = {
  id: number;
  description: string;
  ferry_id: string;
  company_name: string;
};
type UserOption = {
  id: number;
  name: string;
  companies?: { id: number; name: string }[];
};
type CompanyOption = {
  id: number;
  name: string;
  ax_code: string;
  invoice_import_enabled: boolean;
};

type ImportRow = {
  user_id: number | "";
  customer_company_id: number | "";
  route_id: number | "";
  departure_at: string;
  arrival_at: string;
  vehicle_type_id: number | "";
  trailer_type_id: number | "";
  cargo_type_id: number | "";
  vehicle_number: string;
  trailer_number: string;
  vehicle_length: number;
  vehicle_width: number;
  vehicle_weight: number;
  plug_in: boolean;
  adr: boolean;
  temperature: string;
  customer_reference: string;
  booking_reference: string;
  drivers: {
    first_name: string;
    last_name: string;
    document_number: string;
  }[];
  source: Record<string, string>;
};

type RowValidationError = {
  row: number;
  message: string;
};

const parseDriverName = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return { first_name: "", last_name: "" };
  }

  if (parts.length === 1) {
    return { first_name: parts[0], last_name: "-" };
  }

  return {
    first_name: parts[0],
    last_name: parts.slice(1).join(" "),
  };
};

const isVehicleTypeOnlyTruck = (code?: VehicleTypeCode) =>
  code === VehicleTypeCode.OnlyTruck;

const isVehicleTypeOther = (code?: VehicleTypeCode) =>
  code === VehicleTypeCode.Other;

const shouldShowTrailerNumber = (code?: VehicleTypeCode) =>
  !isVehicleTypeOnlyTruck(code);

const isTrailerNumberRequired = (code?: VehicleTypeCode) => {
  if (!code || isVehicleTypeOnlyTruck(code)) {
    return false;
  }

  return !isVehicleTypeOther(code);
};

const getVehicleTypeCode = (
  vehicleTypes: VehicleTypeOption[],
  vehicleTypeId: number | ""
): VehicleTypeCode | undefined => {
  if (!vehicleTypeId) {
    return undefined;
  }

  return vehicleTypes.find((vehicleType) => vehicleType.id === vehicleTypeId)
    ?.code;
};

const defaultImportRow = (): ImportRow => ({
  user_id: "",
  customer_company_id: "",
  route_id: "",
  departure_at: "",
  arrival_at: "",
  vehicle_type_id: "",
  trailer_type_id: "",
  cargo_type_id: "",
  vehicle_number: "",
  trailer_number: "",
  vehicle_length: 17,
  vehicle_width: 2.6,
  vehicle_weight: 1,
  plug_in: false,
  adr: false,
  temperature: "",
  customer_reference: "",
  booking_reference: "",
  drivers: [],
  source: {},
});

const validateMappedRows = (rows: ImportRow[]): RowValidationError[] => {
  const errors: RowValidationError[] = [];

  rows.forEach((row, index) => {
    const rowNumber = index + 1;

    if (!row.departure_at) {
      errors.push({
        row: rowNumber,
        message: "Departure date/time is missing.",
      });
    }

    if (!row.vehicle_type_id) {
      errors.push({
        row: rowNumber,
        message: "Vehicle type mapping not found.",
      });
    }

    if (!row.customer_reference && !row.booking_reference) {
      errors.push({
        row: rowNumber,
        message: "Both customer reference and booking reference are empty.",
      });
    }
  });

  return errors;
};

const parsePoDateTime = (date: string, time: string) => {
  const [day, month, year] = date.split("/");
  const yearFull = Number(year) < 100 ? `20${year}` : year;
  const formattedTime = time.padStart(4, "0");

  return `${yearFull}-${month}-${day} ${formattedTime.slice(
    0,
    2
  )}:${formattedTime.slice(2, 4)}:00`;
};

const parseIrishDateTime = (date: string, time: string) => {
  const datePart = date.split(" ")[0];
  const [day, month, year] = datePart.split("/");
  const yearFull = Number(year) < 100 ? `20${year}` : year;
  const [hours, minutes] = time.split(":");

  return `${yearFull}-${month}-${day} ${hours.padStart(
    2,
    "0"
  )}:${minutes.padStart(2, "0")}:00`;
};

const parseDelimitedRow = (line: string, delimiter: "," | ";"): string[] => {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];

    if (char === '"') {
      const nextChar = line[i + 1];

      if (inQuotes && nextChar === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }

      continue;
    }

    if (char === delimiter && !inQuotes) {
      values.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  values.push(current.trim());

  return values;
};

const getClientCode = (companyName: string): ClientCode | null => {
  if (companyName === "P&O Ferries") return "po";
  if (companyName === "Irish Ferries") return "irish";

  return null;
};

const getApiUrl = (path: string) => {
  const protocol = process.env.REACT_APP_PROTOCOL;
  const hostname = process.env.REACT_APP_HOSTNAME;

  if (!protocol || !hostname) {
    return "";
  }

  return `${protocol}${hostname}/api${path}`;
};

const InvoiceImportPage = () => {
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.loginPage.profile);

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | "">("");
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [routes, setRoutes] = useState<RouteOption[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeOption[]>([]);
  const [trailerTypes, setTrailerTypes] = useState<SelectOption[]>([]);
  const [cargoTypes, setCargoTypes] = useState<SelectOption[]>([]);
  const [isLoadingMeta, setIsLoadingMeta] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);

  const token = localStorage.getItem("token");
  const isInternal = profile ? hasRole(profile, RoleCode.Internal) : false;
  const selectClassName = (value: number | "") =>
    isSubmitAttempted && !value ? "is-invalid" : undefined;
  const textInputClassName = (value: string, isRequired: boolean) =>
    isSubmitAttempted && isRequired && !value.trim() ? "is-invalid" : undefined;

  const renderSourceTag = (row: ImportRow, key: string) => {
    const value = row.source[key];

    if (!value) return null;

    return (
      <div className={styles.tags}>
        <Form.Text className="text-muted">{value}</Form.Text>
      </div>
    );
  };

  const vehicleTypeDefaultId = useMemo(
    () =>
      vehicleTypes.find((item) => item.name.toLowerCase().includes("artic"))
        ?.id ?? "",
    [vehicleTypes]
  );
  const trailerTypeDefaultId = useMemo(
    () => trailerTypes[0]?.id ?? "",
    [trailerTypes]
  );
  const cargoTypeDefaultId = useMemo(
    () =>
      cargoTypes.find((item) => item.name.toLowerCase().includes("unknown"))
        ?.id ??
      cargoTypes[0]?.id ??
      "",
    [cargoTypes]
  );
  const invoiceCompanies = useMemo(
    () =>
      companies.filter((company) => Boolean(company.invoice_import_enabled)),
    [companies]
  );
  const selectedInvoiceCompany = invoiceCompanies.find(
    (company) => company.id === selectedCompanyId
  );
  const selectedInvoiceClientCode = getClientCode(
    selectedInvoiceCompany?.name ?? ""
  );

  const fetchJson = useCallback(
    async (path: string) => {
      const response = await fetch(getApiUrl(path), {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return response.json();
    },
    [token]
  );

  const loadMeta = useCallback(async () => {
    if (!token) return;

    try {
      setIsLoadingMeta(true);
      const [
        routesResponse,
        companiesResponse,
        usersResponse,
        vehicleResponse,
        trailerResponse,
        cargoResponse,
      ] = await Promise.all([
        fetchJson("/routes"),
        fetchJson("/companies?per_page=1000"),
        fetchJson("/users?search=&only_with_companies=1"),
        fetchJson("/vehicle-types"),
        fetchJson("/trailer-types"),
        fetchJson("/cargo-types"),
      ]);

      setRoutes(routesResponse.data ?? []);
      setCompanies(companiesResponse.data ?? []);
      setUsers(usersResponse.data ?? []);
      setVehicleTypes(vehicleResponse.data ?? []);
      setTrailerTypes(trailerResponse.data ?? []);
      setCargoTypes(cargoResponse.data ?? []);
    } catch (error) {
      dispatch(showError("Failed to load import metadata."));
    } finally {
      setIsLoadingMeta(false);
    }
  }, [dispatch, fetchJson, token]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  const resolveRoute = (from: string, to: string, client: ClientCode) => {
    const mappingKey = getRouteMappingKey(from, to);

    if (!mappingKey) {
      return "";
    }

    const mapping = CLIENT_ROUTE_MAPPINGS[client][mappingKey];

    return (
      routes.find(
        (route) =>
          normalizeLookupValue(route.ferry_id) ===
            normalizeLookupValue(mapping.ferry_id) &&
          normalizeLookupValue(route.company_name) ===
            normalizeLookupValue(mapping.company_name)
      )?.id ?? ""
    );
  };

  const mapPoXml = (xmlText: string, client: ClientCode): ImportRow[] => {
    const parser = new DOMParser();
    const document = parser.parseFromString(xmlText, "text/xml");
    const nodes = Array.from(document.getElementsByTagName("DocumentDetails"));

    return nodes.map((node) => {
      const ref = node.getElementsByTagName("Ref")[0]?.textContent ?? "";
      const from = node.getElementsByTagName("RouteFrom")[0]?.textContent ?? "";
      const to = node.getElementsByTagName("RouteTo")[0]?.textContent ?? "";
      const vehicle = node.getElementsByTagName("RegNo1")[0]?.textContent ?? "";
      const trailer = node.getElementsByTagName("RegNo2")[0]?.textContent ?? "";
      const date =
        node.getElementsByTagName("TravelDate")[0]?.textContent ?? "";
      const time =
        node.getElementsByTagName("TravelTime")[0]?.textContent ?? "";
      const lengthRaw =
        node
          .getElementsByTagName("AmountFreight")[0]
          ?.getElementsByTagName("Length")[0]
          ?.textContent?.replace(/[^\d.]/g, "") ?? "17";
      const weightRaw = (
        node.getElementsByTagName("LoadWeight")[0]?.textContent ?? "0"
      ).replace(/[^\d.]/g, "");
      const waybill =
        node.getElementsByTagName("Waybill")[0]?.textContent ?? "";
      const firstDriverName =
        node.getElementsByTagName("NameOfFirstDriver")[0]?.textContent ?? "";
      const driver = parseDriverName(firstDriverName);

      const departureAt = parsePoDateTime(date, time);

      return {
        ...defaultImportRow(),
        route_id: resolveRoute(from, to, client),
        vehicle_type_id: vehicleTypeDefaultId,
        trailer_type_id: trailerTypeDefaultId,
        cargo_type_id: cargoTypeDefaultId,
        departure_at: departureAt,
        arrival_at: departureAt,
        vehicle_number: vehicle,
        trailer_number: trailer,
        vehicle_length: Number(lengthRaw || 17),
        vehicle_weight: Number(weightRaw || 0) === 0 ? 1 : Number(weightRaw),
        customer_reference: waybill,
        booking_reference: waybill,
        drivers: driver.first_name ? [{ ...driver, document_number: "" }] : [],
        source: {
          po_ref: ref,
          user_id: `<Ref>${ref}</Ref>`,
          route_id: `<RouteFrom>${from}</RouteFrom> <RouteTo>${to}</RouteTo>`,
          departure_at: `<TravelDate>${date}</TravelDate> <TravelTime>${time}</TravelTime>`,
          arrival_at: `<TravelDate>${date}</TravelDate> <TravelTime>${time}</TravelTime>`,
          vehicle_type_id: `<VehicleType>${
            node.getElementsByTagName("VehicleType")[0]?.textContent ?? ""
          }</VehicleType>`,
          trailer_type_id: `<Default>ANY</Default>`,
          cargo_type_id: `<Default>UNKNOWN_CARGO</Default>`,
          vehicle_number: `<RegNo1>${vehicle}</RegNo1>`,
          trailer_number: `<RegNo2>${trailer}</RegNo2>`,
          vehicle_length: `<Length>${lengthRaw}</Length>`,
          vehicle_weight: `<LoadWeight>${weightRaw}</LoadWeight>`,
          customer_reference: `<Waybill>${waybill}</Waybill>`,
          booking_reference: `<Waybill>${waybill}</Waybill>`,
          driver_0_first_name: `<NameOfFirstDriver>${firstDriverName}</NameOfFirstDriver>`,
          driver_0_last_name: `<NameOfFirstDriver>${firstDriverName}</NameOfFirstDriver>`,
          driver_0_document_number: `<Default></Default>`,
        },
      };
    });
  };

  const mapIrishCsv = (csvText: string, client: ClientCode): ImportRow[] => {
    const lines = csvText.split(/\r?\n/).filter(Boolean);

    if (lines.length <= 1) return [];

    const semicolonCount = (lines[0].match(/;/g) ?? []).length;
    const commaCount = (lines[0].match(/,/g) ?? []).length;
    const delimiter: "," | ";" = commaCount > semicolonCount ? "," : ";";
    const headers = parseDelimitedRow(lines[0], delimiter);
    const records = lines.slice(1).map((line) => {
      const cols = parseDelimitedRow(line, delimiter);
      return headers.reduce<Record<string, string>>((acc, header, idx) => {
        acc[header] = cols[idx] ?? "";
        return acc;
      }, {});
    });

    return records
      .filter((record) => !(record.CustomerRef ?? "").startsWith("5"))
      .map((record) => {
        const driver1 = parseDriverName(record.Driver1 ?? "");
        const driver2 = parseDriverName(record.Driver2 ?? "");
        const departureAt = parseIrishDateTime(
          record.SailingDate ?? "",
          record.SailingTime ?? "00:00"
        );

        const drivers = [driver1, driver2]
          .filter((driver) => driver.first_name)
          .map((driver) => ({
            ...driver,
            document_number: "",
          }));

        const routeParts = (record.Route ?? "")
          .split("-")
          .map((part) => part.trim());
        const from = routeParts[0] ?? "";
        const to = routeParts[1] ?? "";
        const hazardousDesc = (record.HazardousDesc ?? "").trim();

        return {
          ...defaultImportRow(),
          route_id: resolveRoute(from, to, client),
          vehicle_type_id: vehicleTypeDefaultId,
          trailer_type_id: trailerTypeDefaultId,
          cargo_type_id: cargoTypeDefaultId,
          departure_at: departureAt,
          arrival_at: departureAt,
          vehicle_number: record.VehicleReg ?? "",
          trailer_number: record.Unit ?? "",
          vehicle_length: Number(record.Length ?? 17),
          vehicle_weight:
            Number(record.Weight ?? 0) === 0 ? 1 : Number(record.Weight ?? 1),
          adr: Boolean(hazardousDesc),
          customer_reference: record.CustomerRef ?? "",
          booking_reference: record.BookingRef ?? "",
          drivers,
          source: {
            user_id: `CustomerRef: ${record.CustomerRef ?? ""}`,
            route_id: `Route: ${record.Route ?? ""}`,
            departure_at: `SailingDate: ${
              record.SailingDate ?? ""
            } | SailingTime: ${record.SailingTime ?? ""}`,
            arrival_at: `SailingDate: ${
              record.SailingDate ?? ""
            } | SailingTime: ${record.SailingTime ?? ""}`,
            vehicle_type_id: `UnitType: ${record.UnitType ?? ""}`,
            trailer_type_id: `Default: ANY`,
            cargo_type_id: `Goods: ${record.Goods ?? ""}`,
            vehicle_number: `VehicleReg: ${record.VehicleReg ?? ""}`,
            trailer_number: `Unit: ${record.Unit ?? ""}`,
            vehicle_length: `Length: ${record.Length ?? ""}`,
            vehicle_weight: `Weight: ${record.Weight ?? ""}`,
            booking_reference: `BookingRef: ${record.BookingRef ?? ""}`,
            driver_0_first_name: `Driver1: ${record.Driver1 ?? ""}`,
            driver_0_last_name: `Driver1: ${record.Driver1 ?? ""}`,
            driver_0_document_number: `Default: empty`,
            driver_1_first_name: `Driver2: ${record.Driver2 ?? ""}`,
            driver_1_last_name: `Driver2: ${record.Driver2 ?? ""}`,
            driver_1_document_number: `Default: empty`,
          },
        };
      });
  };

  const onClientChange = async (event: ChangeEvent<HTMLSelectElement>) => {
    const selected = event.target.value ? Number(event.target.value) : "";
    setSelectedCompanyId(selected);
    setRows([]);
    setIsSubmitAttempted(false);
  };

  const onFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;
    setIsSubmitAttempted(false);

    const selectedCompany = invoiceCompanies.find(
      (company) => company.id === selectedCompanyId
    );

    if (!selectedCompany) {
      dispatch(
        showError("Select 'Invoice issued by' before uploading a file.")
      );
      return;
    }

    const fileName = file.name.toLowerCase();
    const client = getClientCode(selectedCompany.name);

    if (!client) {
      dispatch(
        showError(
          "Unsupported invoice issuer. Only P&O Ferries and Irish Ferries are supported."
        )
      );
      return;
    }

    if (client === "po" && !fileName.endsWith(".xml")) {
      dispatch(showError("P&O Ferries import allows only .xml files."));
      return;
    }

    if (client === "irish" && !fileName.endsWith(".csv")) {
      dispatch(showError("Irish Ferries import allows only .csv files."));
      return;
    }

    // File extension already validated against issuer; parser follows issuer.

    const text = await file.text();
    const mappedRows =
      client === "po" ? mapPoXml(text, client) : mapIrishCsv(text, client);

    if (mappedRows.length === 0) {
      dispatch(showError("No orders could be parsed from the uploaded file."));
      setRows([]);
      return;
    }

    const validationErrors = validateMappedRows(mappedRows);

    if (validationErrors.length > 0) {
      const firstErrors = validationErrors
        .slice(0, 5)
        .map((error) => `Row ${error.row}: ${error.message}`)
        .join(" ");
      const suffix =
        validationErrors.length > 5
          ? ` ...and ${validationErrors.length - 5} more mapping errors.`
          : "";

      dispatch(showError(`Mapping error detected. ${firstErrors}${suffix}`));
      setRows([]);
      return;
    }

    if (client === "po") {
      const refToAxCode: Record<string, string> = {
        JJL: "LT_LONGAKL",
        PSA: "LT_AXIS",
        EW: "LT_GIRTLOG",
        GKD: "LT_DELAKL",
        GKEU: "PL_EUSOF",
      };

      const rowsWithCustomer = mappedRows.map((row) => {
        const poRef = (row.source.po_ref ?? "").trim().toUpperCase();
        const axCode = refToAxCode[poRef];

        if (!axCode) {
          return {
            ...row,
            customer_company_id: "" as "",
            user_id: "" as "",
          };
        }

        const matchedCompany = companies.find(
          (company) =>
            (company.ax_code ?? "").trim().toUpperCase() ===
            axCode.trim().toUpperCase()
        );
        const matchedUser = users.find((user) =>
          (user.companies ?? []).some(
            (company) => company.id === matchedCompany?.id
          )
        );

        return {
          ...row,
          customer_company_id: matchedCompany?.id ?? ("" as ""),
          user_id: matchedUser ? matchedUser.id : ("" as ""),
        };
      });

      setRows(rowsWithCustomer);
      return;
    }

    if (client === "irish") {
      const customerRefToAxCode: Record<string, string> = {
        PSSKT: "PL_SKATKL",
        EVERWEST: "LT_GIRTLOG",
        GKRA: "PL_RAWAKL",
        GKDL: "LT_DELAKL",
        PSA: "LT_AXIS",
      };

      const rowsWithCustomer = mappedRows.map((row) => {
        const customerRef = (row.customer_reference ?? "").trim().toUpperCase();
        const axCode = customerRefToAxCode[customerRef];

        if (!axCode) {
          return {
            ...row,
            customer_company_id: "" as "",
            user_id: "" as "",
          };
        }

        const matchedCompany = companies.find(
          (company) =>
            (company.ax_code ?? "").trim().toUpperCase() ===
            axCode.trim().toUpperCase()
        );
        const matchedUser = users.find((user) =>
          (user.companies ?? []).some(
            (company) => company.id === matchedCompany?.id
          )
        );

        return {
          ...row,
          customer_company_id: matchedCompany?.id ?? ("" as ""),
          user_id: matchedUser ? matchedUser.id : ("" as ""),
        };
      });

      setRows(rowsWithCustomer);
      return;
    }

    setRows(mappedRows);
  };

  const updateRow = (
    rowIndex: number,
    key: keyof ImportRow,
    value: string | number | boolean
  ) => {
    setRows((prevRows) =>
      prevRows.map((row, index) =>
        index === rowIndex ? { ...row, [key]: value } : row
      )
    );
  };

  const updateVehicleType = (rowIndex: number, vehicleTypeId: number | "") => {
    setRows((prevRows) =>
      prevRows.map((row, index) => {
        if (index !== rowIndex) {
          return row;
        }

        const vehicleTypeCode = getVehicleTypeCode(vehicleTypes, vehicleTypeId);

        return {
          ...row,
          vehicle_type_id: vehicleTypeId,
          trailer_number: isVehicleTypeOnlyTruck(vehicleTypeCode)
            ? ""
            : row.trailer_number,
        };
      })
    );
  };

  const updateCustomerCompany = (rowIndex: number, companyId: number | "") => {
    setRows((prevRows) =>
      prevRows.map((row, index) => {
        if (index !== rowIndex) return row;

        if (!companyId) {
          return {
            ...row,
            customer_company_id: "",
            user_id: "",
          };
        }

        const matchedUser = users.find((user) =>
          (user.companies ?? []).some((company) => company.id === companyId)
        );

        return {
          ...row,
          customer_company_id: companyId,
          user_id: matchedUser?.id ?? "",
        };
      })
    );
  };

  const updateDriver = (
    rowIndex: number,
    driverIndex: number,
    key: "first_name" | "last_name" | "document_number",
    value: string
  ) => {
    setRows((prevRows) =>
      prevRows.map((row, index) => {
        if (index !== rowIndex) return row;

        const drivers = [...row.drivers];

        if (!drivers[driverIndex]) {
          drivers[driverIndex] = {
            first_name: "",
            last_name: "",
            document_number: "",
          };
        }

        drivers[driverIndex] = {
          ...drivers[driverIndex],
          [key]: value,
        };

        return { ...row, drivers };
      })
    );
  };

  const submitImport = async () => {
    setIsSubmitAttempted(true);

    if (!rows.length || !token) return;

    if (!selectedCompanyId) {
      dispatch(showError("Client must be selected before submit."));
      return;
    }

    if (rows.some((row) => !row.customer_company_id)) {
      dispatch(
        showError(
          "Customer company must be selected for all rows before submit."
        )
      );
      return;
    }

    if (rows.some((row) => !row.route_id)) {
      dispatch(showError("Route must be selected for all rows before submit."));
      return;
    }

    if (
      rows.some((row) =>
        isTrailerNumberRequired(
          getVehicleTypeCode(vehicleTypes, row.vehicle_type_id)
        )
          ? !row.trailer_number.trim()
          : false
      )
    ) {
      dispatch(
        showError(
          "Trailer number is required for all applicable rows before submit."
        )
      );
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await fetch(getApiUrl("/invoice-import"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          invoice_issued_by: selectedCompanyId,
          rows: rows.map(
            ({
              arrival_at: _arrivalAt,
              vehicle_width: _vehicleWidth,
              customer_reference: customerReference,
              temperature: _temperature,
              plug_in: _plugIn,
              user_id: _userId,
              ...row
            }) => ({
              ...row,
              customer_reference: customerReference,
              arrival_at: row.departure_at,
              duration: "00:00:00",
              company_id: row.customer_company_id || null,
            })
          ),
        }),
      });

      if (!response.ok) {
        dispatch(showError("Failed to queue import."));
        return;
      }

      dispatch(showSuccess("Orders import queued successfully."));
      setRows([]);
      setSelectedCompanyId("");
      setIsSubmitAttempted(false);
    } catch (error) {
      dispatch(showError("Failed to queue import."));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isInternal) {
    return (
      <Container fluid className={styles.card}>
        <Row>
          <Col>
            <Card>
              <Card.Body>
                <Alert variant="danger" className="mb-0">
                  This page is available only for Internal role.
                </Alert>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className={styles.card}>
      <Row>
        <Col>
          <Card>
            <Card.Body>
              <div className={styles.cardTitle}>
                <h5 className="mb-0">Import</h5>
              </div>

              <hr />

              {isLoadingMeta && (
                <div className={styles.loading}>
                  <Spinner animation="border" className="text-primary" />
                </div>
              )}

              {!isLoadingMeta && (
                <Row className="g-3 align-items-start">
                  <Col md={4}>
                    <Form.Label>Invoice issued by</Form.Label>
                    <Form.Select
                      value={selectedCompanyId}
                      onChange={onClientChange}
                      className={selectClassName(selectedCompanyId)}
                    >
                      <option value="">Select company</option>
                      {invoiceCompanies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  {!!selectedCompanyId && (
                    <Col md={8}>
                      <Form.Label>Invoice file</Form.Label>
                      <Form.Control
                        type="file"
                        onChange={onFileChange}
                        accept={
                          selectedInvoiceClientCode === "po"
                            ? ".xml,text/xml"
                            : ".csv,text/csv"
                        }
                      />
                      {selectedInvoiceClientCode === "po" && (
                        <Form.Text className="text-muted">
                          P&O Ferries accepts only .xml files.
                        </Form.Text>
                      )}
                      {selectedInvoiceClientCode === "irish" && (
                        <Form.Text className="text-muted">
                          Irish Ferries accepts only .csv files.
                        </Form.Text>
                      )}
                    </Col>
                  )}
                </Row>
              )}

              {!!rows.length && (
                <>
                  <div className={styles.tableWrap}>
                    <Table
                      bordered
                      responsive
                      size="sm"
                      className={styles.rowsTable}
                    >
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Company</th>
                          <th>Route</th>
                          <th>Departure</th>
                          <th>Vehicle type</th>
                          <th>Trailer type</th>
                          <th>Cargo type</th>
                          <th>Vehicle no.</th>
                          <th>Trailer no.</th>
                          <th>Length</th>
                          <th>Weight</th>
                          <th>Customer ref</th>
                          <th>Booking ref</th>
                          <th>Driver1 (F/L)</th>
                          <th>Driver2 (F/L)</th>
                          <th>ADR</th>
                        </tr>
                      </thead>
                      <tbody style={{ borderTop: "0px" }}>
                        {rows.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            <td>{rowIndex + 1}</td>
                            <td>
                              <Form.Select
                                size="sm"
                                value={row.customer_company_id}
                                className={selectClassName(
                                  row.customer_company_id
                                )}
                                onChange={(event) =>
                                  updateCustomerCompany(
                                    rowIndex,
                                    event.target.value
                                      ? Number(event.target.value)
                                      : ""
                                  )
                                }
                              >
                                <option value="">
                                  Select customer company
                                </option>
                                {companies.map((company) => (
                                  <option key={company.id} value={company.id}>
                                    {company.name}
                                  </option>
                                ))}
                              </Form.Select>
                              {renderSourceTag(row, "user_id")}
                            </td>
                            <td>
                              <Form.Select
                                size="sm"
                                value={row.route_id}
                                className={selectClassName(row.route_id)}
                                onChange={(event) =>
                                  updateRow(
                                    rowIndex,
                                    "route_id",
                                    Number(event.target.value)
                                  )
                                }
                              >
                                <option value="">Select route</option>
                                {routes.map((route) => (
                                  <option key={route.id} value={route.id}>
                                    {route.description}
                                  </option>
                                ))}
                              </Form.Select>
                              {renderSourceTag(row, "route_id")}
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                value={row.departure_at}
                                onChange={(event) =>
                                  updateRow(
                                    rowIndex,
                                    "departure_at",
                                    event.target.value
                                  )
                                }
                              />
                              {renderSourceTag(row, "departure_at")}
                            </td>
                            <td>
                              <Form.Select
                                size="sm"
                                value={row.vehicle_type_id}
                                className={selectClassName(row.vehicle_type_id)}
                                onChange={(event) =>
                                  updateVehicleType(
                                    rowIndex,
                                    event.target.value
                                      ? Number(event.target.value)
                                      : ""
                                  )
                                }
                              >
                                <option value="">Select</option>
                                {vehicleTypes.map((item) => (
                                  <option key={item.id} value={item.id}>
                                    {item.name}
                                  </option>
                                ))}
                              </Form.Select>
                              {renderSourceTag(row, "vehicle_type_id")}
                            </td>
                            <td>
                              <Form.Select
                                size="sm"
                                value={row.trailer_type_id}
                                className={selectClassName(row.trailer_type_id)}
                                onChange={(event) =>
                                  updateRow(
                                    rowIndex,
                                    "trailer_type_id",
                                    Number(event.target.value)
                                  )
                                }
                              >
                                <option value="">Select</option>
                                {trailerTypes.map((item) => (
                                  <option key={item.id} value={item.id}>
                                    {item.name}
                                  </option>
                                ))}
                              </Form.Select>
                              {renderSourceTag(row, "trailer_type_id")}
                            </td>
                            <td>
                              <Form.Select
                                size="sm"
                                value={row.cargo_type_id}
                                className={selectClassName(row.cargo_type_id)}
                                onChange={(event) =>
                                  updateRow(
                                    rowIndex,
                                    "cargo_type_id",
                                    Number(event.target.value)
                                  )
                                }
                              >
                                <option value="">Select</option>
                                {cargoTypes.map((item) => (
                                  <option key={item.id} value={item.id}>
                                    {item.name}
                                  </option>
                                ))}
                              </Form.Select>
                              {renderSourceTag(row, "cargo_type_id")}
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                value={row.vehicle_number}
                                onChange={(event) =>
                                  updateRow(
                                    rowIndex,
                                    "vehicle_number",
                                    event.target.value
                                  )
                                }
                              />
                              {renderSourceTag(row, "vehicle_number")}
                            </td>
                            <td>
                              {shouldShowTrailerNumber(
                                getVehicleTypeCode(
                                  vehicleTypes,
                                  row.vehicle_type_id
                                )
                              ) ? (
                                <Form.Control
                                  size="sm"
                                  value={row.trailer_number}
                                  className={textInputClassName(
                                    row.trailer_number,
                                    isTrailerNumberRequired(
                                      getVehicleTypeCode(
                                        vehicleTypes,
                                        row.vehicle_type_id
                                      )
                                    )
                                  )}
                                  onChange={(event) =>
                                    updateRow(
                                      rowIndex,
                                      "trailer_number",
                                      event.target.value
                                    )
                                  }
                                />
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                              {renderSourceTag(row, "trailer_number")}
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="number"
                                value={row.vehicle_length}
                                onChange={(event) =>
                                  updateRow(
                                    rowIndex,
                                    "vehicle_length",
                                    Number(event.target.value)
                                  )
                                }
                              />
                              {renderSourceTag(row, "vehicle_length")}
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                type="number"
                                value={row.vehicle_weight}
                                onChange={(event) =>
                                  updateRow(
                                    rowIndex,
                                    "vehicle_weight",
                                    Number(event.target.value)
                                  )
                                }
                              />
                              {renderSourceTag(row, "vehicle_weight")}
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                value={row.customer_reference}
                                onChange={(event) =>
                                  updateRow(
                                    rowIndex,
                                    "customer_reference",
                                    event.target.value
                                  )
                                }
                              />
                              {renderSourceTag(row, "customer_reference")}
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                value={row.booking_reference}
                                onChange={(event) =>
                                  updateRow(
                                    rowIndex,
                                    "booking_reference",
                                    event.target.value
                                  )
                                }
                              />
                              {renderSourceTag(row, "booking_reference")}
                            </td>
                            <td>
                              <div className={styles.driverCell}>
                                <Form.Control
                                  size="sm"
                                  placeholder="First"
                                  value={row.drivers[0]?.first_name ?? ""}
                                  onChange={(event) =>
                                    updateDriver(
                                      rowIndex,
                                      0,
                                      "first_name",
                                      event.target.value
                                    )
                                  }
                                />
                                <Form.Control
                                  size="sm"
                                  placeholder="Last"
                                  value={row.drivers[0]?.last_name ?? ""}
                                  onChange={(event) =>
                                    updateDriver(
                                      rowIndex,
                                      0,
                                      "last_name",
                                      event.target.value
                                    )
                                  }
                                />
                              </div>
                              {renderSourceTag(row, "driver_0_first_name")}
                            </td>
                            <td>
                              <div className={styles.driverCell}>
                                <Form.Control
                                  size="sm"
                                  placeholder="First"
                                  value={row.drivers[1]?.first_name ?? ""}
                                  onChange={(event) =>
                                    updateDriver(
                                      rowIndex,
                                      1,
                                      "first_name",
                                      event.target.value
                                    )
                                  }
                                />
                                <Form.Control
                                  size="sm"
                                  placeholder="Last"
                                  value={row.drivers[1]?.last_name ?? ""}
                                  onChange={(event) =>
                                    updateDriver(
                                      rowIndex,
                                      1,
                                      "last_name",
                                      event.target.value
                                    )
                                  }
                                />
                              </div>
                              {renderSourceTag(row, "driver_1_first_name")}
                            </td>
                            <td>
                              <Form.Check
                                type="checkbox"
                                checked={row.adr}
                                onChange={(event) =>
                                  updateRow(
                                    rowIndex,
                                    "adr",
                                    event.target.checked
                                  )
                                }
                              />
                            </td>
                            <td></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  <div className={styles.submit}>
                    <Button
                      onClick={submitImport}
                      disabled={isSubmitting || !selectedCompanyId}
                    >
                      {isSubmitting ? "Submitting..." : "Submit"}
                    </Button>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default InvoiceImportPage;
